<template>
  <div class="message-icon-wrapper">
    <div
      class="customize-icon"
      @click.stop="toggleMessageMenu"
    >
      <img
        :src="showMessageMenu ? require('@/assets/icons/message_click.png') : require('@/assets/icons/message.png')"
        :alt="$t('message.altIcon')"
        class="icon-img"
      >
      <!-- 未读消息小红点 -->
      <span
        v-if="totalUnreadCount > 0"
        class="message-badge-dot"
      />
    </div>
    <!-- 消息下拉菜单 -->
    <div
      v-if="showMessageMenu"
      class="message-menu-panel"
      @click.stop
    >
      <!-- 头部 -->
      <div class="message-menu-header">
        <span class="message-menu-title">{{ $t('message.unreadTitle') }}</span>
        <span
          class="message-menu-all"
          @click="goToMessageCenter"
        >{{ $t('message.allMessages') }}</span>
      </div>
      <!-- 标签页 -->
      <div class="message-menu-tabs">
        <div
          class="message-tab-item"
          :class="{ active: activeMessageTab === 'system' }"
          @click="switchMessageTab('system')"
        >
          {{ $t('message.system') }}
          <span
            v-if="unreadCounts.system >= 0"
            class="tab-badge"
            :class="{ 'badge-red': unreadCounts.system > 0, 'badge-gray': unreadCounts.system === 0 }"
          >{{ unreadCounts.system }}</span>
        </div>
        <div
          class="message-tab-item"
          :class="{ active: activeMessageTab === 'business' }"
          @click="switchMessageTab('business')"
        >
          {{ $t('message.business') }}
          <span
            v-if="unreadCounts.business >= 0"
            class="tab-badge"
            :class="{ 'badge-red': unreadCounts.business > 0, 'badge-gray': unreadCounts.business === 0 }"
          >{{ unreadCounts.business }}</span>
        </div>
        <div
          class="message-tab-item"
          :class="{ active: activeMessageTab === 'activity' }"
          @click="switchMessageTab('activity')"
        >
          {{ $t('message.activity') }}
          <span
            v-if="unreadCounts.activity >= 0"
            class="tab-badge"
            :class="{ 'badge-red': unreadCounts.activity > 0, 'badge-gray': unreadCounts.activity === 0 }"
          >{{ unreadCounts.activity }}</span>
        </div>
      </div>
      <!-- 消息列表 -->
      <div class="message-list">
        <div
          v-if="messageLoading"
          class="message-loading"
        >
          {{ $t('common.loading') }}
        </div>
        <template v-else>
          <div
            v-for="(message, index) in currentMessageList"
            :key="message.id || index"
            class="message-item"
            @click="handleMessageClick(message)"
          >
            <div class="message-header">
              <div class="message-title">
                {{ message.title }}
              </div>
              <div class="message-time">
                {{ message.createTime }}
              </div>
            </div>
            <div
              class="message-content"
              v-html="formatMessageContent(message.message)"
            />
          </div>
          <div
            v-if="!currentMessageList || currentMessageList.length === 0"
            class="message-empty"
          >
            {{ $t('message.noMessage') }}
          </div>
        </template>
      </div>
    </div>
    <!-- 遮罩层，点击关闭菜单 -->
    <div
      v-if="showMessageMenu"
      class="message-menu-overlay"
      @click="closeMessageMenu"
    />

    <!-- 消息详情弹窗 -->
    <MessageDetailDialog
      v-model="showMessageDetailDialog"
      :read-record-id="currentReadRecordId"
      @close="handleMessageDetailClose"
      @message-read="handleMessageRead"
    />
  </div>
</template>

<script>
import MessageDetailDialog from '@/components/MessageDetailDialog/index.vue'
import { getMessageList, getUnreadMessageCount } from '@/api/message'
import { MessageTypeEnum } from '@/enums/messageType'
import { formatMessageContent } from '@/utils/messageUtils'
/**
 * 消息刷新间隔时间
 * 60秒
 */
const MESSAGE_REFRESH_INTERVAL = 60000

export default {
  name: 'MessageList',
  components: {
    MessageDetailDialog
  },
  props: {
    userLoggedIn: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showMessageMenu: false,
      activeMessageTab: 'system',
      unreadCounts: {
        system: 0,
        business: 0,
        activity: 0
      },
      messageList: {
        system: [],
        business: [],
        activity: []
      },
      messageLoading: false,
      showMessageDetailDialog: false,
      currentReadRecordId: null,
      messageRefreshTimer: null
    }
  },
  computed: {
    totalUnreadCount() {
      return (this.unreadCounts.system || 0) + (this.unreadCounts.business || 0) + (this.unreadCounts.activity || 0)
    },
    currentMessageList() {
      if (!this.messageList || !this.activeMessageTab) {
        return []
      }
      return Array.isArray(this.messageList[this.activeMessageTab]) 
        ? this.messageList[this.activeMessageTab] 
        : []
    }
  },
  watch: {
    userLoggedIn: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadUnreadMessageCount()
          this.startMessageRefreshTimer()
        } else {
          this.stopMessageRefreshTimer()
          this.resetMessageData()
        }
      }
    }
  },
  beforeUnmount() {
    this.stopMessageRefreshTimer()
  },
  methods: {
    formatMessageContent,
    toggleMessageMenu() {
      this.showMessageMenu = !this.showMessageMenu
      if (this.showMessageMenu) {
        this.$emit('menu-toggle', 'message')
        if (this.userLoggedIn) {
          this.loadMessageList(this.activeMessageTab)
        }
      }
    },
    closeMessageMenu() {
      this.showMessageMenu = false
    },
    resetMessageData() {
      this.unreadCounts = {
        system: 0,
        business: 0,
        activity: 0
      }
      this.messageList = {
        system: [],
        business: [],
        activity: []
      }
    },
    switchMessageTab(tab) {
      this.activeMessageTab = tab
      this.loadMessageList(tab)
    },
    startMessageRefreshTimer() {
      this.stopMessageRefreshTimer()
      this.messageRefreshTimer = window.setInterval(() => {
        this.refreshMessageData()
      }, MESSAGE_REFRESH_INTERVAL)
    },
    stopMessageRefreshTimer() {
      if (!this.messageRefreshTimer) return
      window.clearInterval(this.messageRefreshTimer)
      this.messageRefreshTimer = null
    },
    async refreshMessageData() {
      if (!this.userLoggedIn) {
        this.resetMessageData()
        return
      }
      await this.loadUnreadMessageCount()
      if (this.showMessageMenu) {
        this.messageList[this.activeMessageTab] = []
        await this.loadMessageList(this.activeMessageTab)
      }
    },
    async loadUnreadMessageCount() {
      try {
        const response = await getUnreadMessageCount()
        if (response && response.code === 200) {
          this.unreadCounts = response.data
        }
      } catch (error) {
        console.error('加载未读消息数量失败:', error)
        this.unreadCounts = {}
      }
    },
    async loadMessageList(tab) {
      if (this.messageList[tab] && this.messageList[tab].length > 0) {
        return
      }

      this.messageList[tab] = []
      this.messageLoading = true
      try {
        const params = {
          isRead: '0',
          pageNum: 1,
          pageSize: 99
        }

        const messageTypeMap = {
          'system': MessageTypeEnum.SYSTEM,
          'business': MessageTypeEnum.BUSINESS,
          'activity': MessageTypeEnum.ACTIVITY
        }
        const messageType = messageTypeMap[tab]
        if (messageType) {
          params.type = messageType.code
        }

        const response = await getMessageList(params)
        if (response && response.code === 200) {
          this.messageList[tab] = response.rows
        } else {
          this.messageList[tab] = []
        }
      } catch (error) {
        console.error('加载消息列表失败:', error)
        this.messageList[tab] = []
      } finally {
        this.messageLoading = false
      }
    },
    goToMessageCenter() {
      this.closeMessageMenu()
      this.$router.push('/user-center/message')
    },
    handleMessageClick(message) {
      this.currentReadRecordId = message.readRecordId
      this.showMessageDetailDialog = true
    },
    handleMessageDetailClose() {
      this.currentReadRecordId = null
      this.showMessageDetailDialog = false
    },
    handleMessageRead(readRecordId) {
      const messageTypes = ['system', 'business', 'activity']
      messageTypes.forEach(type => {
        if (this.messageList[type] && Array.isArray(this.messageList[type])) {
          const index = this.messageList[type].findIndex(msg => msg.readRecordId === readRecordId)
          if (index !== -1) {
            this.messageList[type].splice(index, 1)
          }
        }
      })
      if (this.userLoggedIn) {
        this.loadUnreadMessageCount()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.message-icon-wrapper {
  position: relative;

  .customize-icon {
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 4px 8px;
    transition: opacity 0.3s;
    position: relative;

    .icon-img {
      width: 23px;
      height: 23px;
      display: block;
      transition: opacity 0.3s;
    }

    &:hover .icon-img {
      opacity: 0.7;
    }

    // 未读消息小红点
    .message-badge-dot {
      position: absolute;
      top: 2px;
      right: 2px;
      width: 8px;
      height: 8px;
      background-color: #f56c6c;
      border-radius: 50%;
      border: 2px solid #ffffff;
      animation: breathe 2s ease-in-out infinite;
    }
  }

  // 消息下拉菜单
  .message-menu-panel {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    min-width: 400px;
    max-width: 500px;
    z-index: 1001;
    overflow: hidden;

    // 三角形指示器
    &::before {
      content: '';
      position: absolute;
      top: -6px;
      right: 20px;
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-bottom: 6px solid #ffffff;
    }

    // 头部
    .message-menu-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 20px;
      border-bottom: 1px solid #f0f0f0;

      .message-menu-title {
        font-size: 16px;
        font-weight: bold;
        color: #000000;
      }

      .message-menu-all {
        font-size: 14px;
        color: #1563e8;
        cursor: pointer;
        transition: color 0.3s;

        &:hover {
          color: #0d63f3;
        }
      }
    }

    // 标签页
    .message-menu-tabs {
      display: flex;
      padding: 0 20px;
      border-bottom: 1px solid #f0f0f0;
      background-color: #fafafa;

      .message-tab-item {
        flex: 1;
        padding: 12px 0;
        text-align: center;
        font-size: 14px;
        color: #606266;
        cursor: pointer;
        position: relative;
        transition: all 0.3s;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;

        &:hover {
          color: #1563e8;
        }

        &.active {
          color: #1563e8;
          font-weight: 600;
          background-color: #ffffff;
          border-bottom: 2px solid #1563e8;
          margin-bottom: -1px;
        }

        .tab-badge {
          min-width: 18px;
          height: 18px;
          padding: 0 5px;
          border-radius: 9px;
          font-size: 11px;
          font-weight: bold;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1;

          &.badge-red {
            background-color: #f56c6c;
            color: #ffffff;
          }

          &.badge-gray {
            background-color: #c0c4cc;
            color: #ffffff;
          }
        }
      }
    }

    // 消息列表
    .message-list {
      max-height: 400px;
      overflow-y: auto;
      padding: 0;

      .message-loading {
        padding: 40px 20px;
        text-align: center;
        color: #909399;
        font-size: 14px;
      }

      .message-item {
        padding: 16px 20px;
        border-bottom: 1px solid #f0f0f0;
        cursor: pointer;
        transition: background-color 0.3s;

        &:hover {
          background-color: #f5f7fa;
        }

        &:last-child {
          border-bottom: none;
        }

        .message-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 8px;
          gap: 12px;

          .message-title {
            font-size: 14px;
            font-weight: 600;
            color: #000000;
            line-height: 1.4;
            margin-bottom: 0;
            flex: 1;
          }

          .message-time {
            font-size: 12px;
            color: #909399;
            white-space: nowrap;
            flex-shrink: 0;
            padding-top: 2px;
          }
        }

        .message-content {
          font-size: 13px;
          color: #606266;
          line-height: 1.5;
          overflow: hidden;
          text-overflow: ellipsis;
          
          :deep(span) {
            display: inline;
          }
          display: -webkit-box;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          -webkit-box-orient: vertical;
        }
      }

      .message-empty {
        padding: 40px 20px;
        text-align: center;
        color: #909399;
        font-size: 14px;
      }
    }
  }

  // 遮罩层
  .message-menu-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    background: transparent;
  }
}

// 呼吸动画
@keyframes breathe {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.2);
  }
}

// 移动端适配
@media (max-width: 768px) {
  .message-icon-wrapper {
    .customize-icon {
      padding: 4px 6px;
      .icon-img {
        width: 20px;
        height: 20px;
      }
    }

    .message-menu-panel {
      position: fixed;
      top: auto;
      bottom: 0;
      left: 0;
      right: 0;
      min-width: 100%;
      max-height: 70vh;
      overflow-y: auto;
      border-radius: 16px 16px 0 0;

      &::before {
        display: none;
      }
    }
  }
}

@media (max-width: 480px) {
  .message-icon-wrapper {
    padding: 4px;
    .icon-img {
      width: 18px;
      height: 18px;
    }

    .message-menu-panel {
      .message-menu-header {
        padding: 12px 16px;
        font-size: 16px;
      }

      .message-menu-tabs {
        padding: 0 16px;
        .message-tab-item {
          padding: 10px 0;
          font-size: 14px;
        }
      }

      .message-list {
        max-height: calc(70vh - 120px);
        .message-item {
          padding: 12px 16px;
          .message-header {
            margin-bottom: 6px;
            .message-title {
              font-size: 14px;
            }
            .message-time {
              font-size: 11px;
            }
          }
          .message-content {
            font-size: 13px;
          }
        }
      }
    }
  }
}
</style>
